import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChartTypes } from '@mode/shared/contract-common';
import { LabeledMenuComponent } from '@mode/shared/ui';
import * as TextFormattingHelpers from './../text-formatting-helpers';

export const BASE_FORMAT: ChartTypes.TextFormat.CurrencyType = {
  ...TextFormattingHelpers.getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.Currency),
};

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, LabeledMenuComponent],
  selector: 'mode-currency-format-pane',
  templateUrl: './currency-format-pane.component.html',
  styleUrls: ['./currency-format-pane.component.scss'],
})
export class CurrencyFormatPaneComponent implements OnChanges {
  @Input({ required: true }) format!: ChartTypes.TextFormat.CurrencyType;

  @Output() formatChange = new EventEmitter<ChartTypes.TextFormat.CurrencyType>();

  readonly Presets = TextFormattingHelpers.CurrencyPresetMenuItems;
  selectedPreset: TextFormattingHelpers.CurrencyPresetMenuItem | undefined;

  readonly DisplayUnits = TextFormattingHelpers.DisplayUnitMenuItems;
  selectedDisplayUnit: TextFormattingHelpers.DisplayUnitMenuItem | undefined;

  readonly ThousandsSeparators = TextFormattingHelpers.ThousandsSeparatorMenuItems;
  selectedThousandsSeparator: TextFormattingHelpers.ThousandsSeparatorMenuItem | undefined;

  readonly ThousandsGroupings = TextFormattingHelpers.ThousandsGroupingMenuItems;
  selectedThousandsGrouping: TextFormattingHelpers.ThousandsGroupingMenuItem | undefined;

  readonly DecimalSeparators = TextFormattingHelpers.DecimalSeparatorMenuItems;
  selectedDecimalSeparator: TextFormattingHelpers.DecimalSeparatorMenuItem | undefined;

  readonly NegativeBehavior = TextFormattingHelpers.NegativeBehaviorMenuItems;
  selectedNegativeBehavior: TextFormattingHelpers.NegativeBehaviorMenuItem | undefined;

  readonly CurrencySymbols = TextFormattingHelpers.SymbolMenuItems;
  selectedPrefixSymbol: TextFormattingHelpers.SymbolMenuItem | undefined;
  selectedSuffixSymbol: TextFormattingHelpers.SymbolMenuItem | undefined;

  isCustomPreset = false;
  isCustomPrefixSelected = false;
  isCustomSuffixSelected = false;

  ngOnChanges(): void {
    this.initSelected();
  }

  initSelected() {
    const format =
      // Mix in the BASE_FORMAT such that we always have
      // the minimum set of properties per the format type
      (this.format = {
        ...BASE_FORMAT,
        ...this.format,
      });

    this.selectedPreset = this.Presets.find((preset) => preset.value?.currencyPreset === format.currencyPreset);
    this.isCustomPreset = this.selectedPreset?.value?.currencyPreset === ChartTypes.CurrencyPreset.Custom;

    this.selectedDisplayUnit = TextFormattingHelpers.findOrFirst(this.DisplayUnits, format.displayUnit ?? '');
    this.selectedThousandsSeparator = TextFormattingHelpers.findOrFirst(
      this.ThousandsSeparators,
      format.separator ?? ''
    );
    this.selectedThousandsGrouping = TextFormattingHelpers.findOrFirst(this.ThousandsGroupings, format.grouping);
    this.selectedDecimalSeparator = TextFormattingHelpers.findOrFirst(
      this.DecimalSeparators,
      format.decimalStyle ?? ''
    );
    this.selectedNegativeBehavior = TextFormattingHelpers.findOrFirst(this.NegativeBehavior, format.negativeBehavior);
    this.selectedPrefixSymbol = this.getSelectedCurrencySymbol(
      format.currencySymbolPrefix ?? '',
      this.isCustomPrefixSelected
    );
    this.isCustomPrefixSelected = this.selectedPrefixSymbol?.value === 'custom';

    this.selectedSuffixSymbol = this.getSelectedCurrencySymbol(
      format.currencySymbolSuffix ?? '',
      this.isCustomSuffixSelected
    );
    this.isCustomSuffixSelected = this.selectedSuffixSymbol?.value === 'custom';
  }

  onPresetSelected(preset: TextFormattingHelpers.CurrencyPresetMenuItem) {
    this.apply({ ...preset.value });
  }

  onDisplayUnitSelected(displayUnit: TextFormattingHelpers.DisplayUnitMenuItem) {
    this.apply({ displayUnit: displayUnit.value });
  }

  onThousandsSeparatorSelected(separator: TextFormattingHelpers.ThousandsSeparatorMenuItem) {
    this.apply({ separator: separator.value });
  }

  onThousandsGroupingSelected(grouping: TextFormattingHelpers.ThousandsGroupingMenuItem) {
    this.apply({ grouping: grouping.value });
  }

  onDecimalSeparatorSelected(separator: TextFormattingHelpers.DecimalSeparatorMenuItem) {
    this.apply({ decimalStyle: separator.value });
  }

  onNegativeBehaviorSelected(behavior: TextFormattingHelpers.NegativeBehaviorMenuItem) {
    this.apply({ negativeBehavior: behavior.value });
  }

  onPrefixSelected(prefix: TextFormattingHelpers.SymbolMenuItem) {
    this.isCustomPrefixSelected = prefix.value === 'custom';
    const currencySymbolPrefix = this.isCustomPrefixSelected ? '' : (prefix.value ?? '');
    this.apply({ currencySymbolPrefix });
  }

  onSuffixSelected(prefix: TextFormattingHelpers.SymbolMenuItem) {
    this.isCustomSuffixSelected = prefix.value === 'custom';
    const currencySymbolSuffix = this.isCustomSuffixSelected ? '' : (prefix.value ?? '');
    this.apply({ currencySymbolSuffix });
  }

  onCurrencyPrefixChange(value: string) {
    this.apply({ currencySymbolPrefix: value });
  }

  onCurrencySuffixChange(value: string) {
    this.apply({ currencySymbolSuffix: value });
  }

  onPrecisionChange(value: unknown) {
    const precision = TextFormattingHelpers.isValidPrecision(value) ? value : BASE_FORMAT.precision;
    this.apply({
      precision,
    });
  }

  apply(props: Partial<ChartTypes.TextFormat.CurrencyType>) {
    this.format = { ...this.format, ...props };
    this.formatChange.emit({
      ...this.format,
    });
    this.initSelected();
  }

  private getSelectedCurrencySymbol(symbol: string, isCustomSelected: boolean) {
    // Find the item associated with one of our known (ie built-in) symbols.
    // If the symbol doesn't match a known symbol, then it is 'custom'.
    // Note: that if the user entered 'custom' as the custom symbol, it will still
    // correctly match the 'Custom' menu item.
    const knownSymbol = this.CurrencySymbols.find((currencySymbol) => currencySymbol.value === symbol);

    if (!knownSymbol || isCustomSelected) {
      // If the user has specifically chosen 'Custom' from the menu, then we want to honor
      // that even if the user enters one of our known symbols (including '').
      return this.CurrencySymbols.find((currencySymbol) => currencySymbol.value === 'custom');
    }
    return knownSymbol;
  }
}
